import React, { useState } from "react";

import Modal, { ModalSize } from "~/common/components/Modal";

import { Button, Stack, TextField } from "~/common/mui";
import { useTranslation } from "react-i18next";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";

interface EditWishlistNamePopupProps {
  onOk: (newName: string) => void;
  onCancel: () => void;
  initialWishlistName?: string;
}

const EditWishlistNamePopup = ({ initialWishlistName, onOk, onCancel }: EditWishlistNamePopupProps) => {
  const [wishlistName, setWishlistName] = useState(initialWishlistName ?? "");
  const [isEditMode] = useState(!!initialWishlistName);

  const [emptyNameError, setEmptyNameError] = useState(false);

  const { t } = useTranslation();

  const screenSize = useNumericScreenSize();
  const isMobile = screenSize <= useNumericScreenSize.phone;

  const confirm = () => {
    const name = wishlistName.trim();
    if (!name) {
      setEmptyNameError(true);
    } else {
      onOk(name);
    }
  };

  const footer = (
    <div className={`d-flex flex-align-center w-100 ${isMobile ? "flex-justify-end" : "flex-justify-between"}`}>
      {!isMobile && <p className={"m-0 title-medium f-carbon"}>{t("editWishlistNamePopup.mandatory")}</p>}
      <Stack direction={"row"} gap={1}>
        <Button size={"medium"} variant={"text"} onClick={onCancel} data-test-id={"add-to-wishlist-btn-cancel"}>
          {t(`editWishlistNamePopup.buttons.${isEditMode ? "edit" : "create"}.cancel`)}
        </Button>
        <Button size={"medium"} onClick={confirm} data-test-id={"add-to-wishlist-btn-ok"}>
          {t(`editWishlistNamePopup.buttons.${isEditMode ? "edit" : "create"}.ok`)}
        </Button>
      </Stack>
    </div>
  );

  const headerLabel = t(`editWishlistNamePopup.header.${isEditMode ? "edit" : "create"}`);

  return (
    <Modal onClose={onCancel} footerChildren={footer} title={headerLabel} size={ModalSize.SMALL}>
      <TextField
        fullWidth
        label={t("editWishlistNamePopup.inputLabel")}
        inputProps={{ "data-test-id": "add-to-wishlist-input-name" }}
        value={wishlistName}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWishlistName(e.target.value)}
        error={emptyNameError}
        sx={{
          marginTop: "8px",
        }}
        helperText={emptyNameError ? t("editWishlistNamePopup.emptyNameError") : undefined}
      />
      {isMobile && <p className={"m-0 mt-2 title-medium f-carbon"}>{t("editWishlistNamePopup.mandatory")}</p>}
    </Modal>
  );
};

export default EditWishlistNamePopup;
